import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { AccommodationCardData, PublicPageProps } from '@/types'

import Link from 'next/link'
import Image from 'next/image'
import { Seo } from '@/components/generals/seo'
import { buttonClass } from '@/components/ui/button'
import { QuickRequest } from '@/components/site/quick-request'
import { AccommodationCard } from '@/components/site/accommodation-card'
import { SectionHeading, SiteLayout } from '@/components/site/site-layout'
import { loadPublicProps, localeOf } from '@/lib/server/ssr'
import { countByCategory, listAccommodations, listLocations } from '@/lib/server/accommodations'
import { plural, useI18n } from '@/i18n'

import {
    ArrowRight,
    Building2,
    CalendarCheck,
    Check,
    Gem,
    House,
    Languages,
    MapPin,
    Send,
    ShieldCheck,
    Waves
} from 'lucide-react'

interface HomeProps extends PublicPageProps {
    featured: Array<AccommodationCardData>
    locations: Array<string>
    counts: { poolVillas: number, houses: number, apartments: number }
}

const HIGHLIGHT_ICONS = [MapPin, Gem, ShieldCheck, Languages]
const STEP_ICONS = [House, Send, CalendarCheck]
const DESTINATION_KEYS = ['Zarzis', 'Djerba']

const Home: FC<HomeProps> = ({ featured, locations, counts, ...base }): ReactNode => {
    const { t } = useI18n()

    const categories = [
        { href: '/accommodations?type=villa&pool=yes', title: t.home.categoryPoolTitle, text: t.home.categoryPoolText, count: counts.poolVillas, icon: Waves },
        { href: '/accommodations?type=house', title: t.home.categoryHouseTitle, text: t.home.categoryHouseText, count: counts.houses, icon: House },
        { href: '/accommodations?type=apartment', title: t.home.categoryApartmentTitle, text: t.home.categoryApartmentText, count: counts.apartments, icon: Building2 }
    ]

    return (
        <SiteLayout {...base} transparentHeader>
            <Seo />

            <section id='hero' className='relative isolate flex min-h-[100svh] flex-col justify-end overflow-hidden pt-28'>
                <Image
                    src='/images/hero-villa.jpg'
                    alt={t.home.heroImageAlt}
                    fill
                    priority
                    sizes='100vw'
                    className='-z-20 object-cover object-[65%_center]'
                />
                <div aria-hidden='true' className='hero-shade absolute inset-0 -z-10' />

                <div className='mx-auto w-full max-w-7xl px-4 pb-10 sm:px-6 lg:px-8 lg:pb-14'>
                    <div className='max-w-3xl animate-in fade-in slide-in-from-bottom-4 duration-700'>
                        <p className='text-xs font-bold tracking-[0.3em] text-gold-soft uppercase'>{t.home.heroEyebrow}</p>
                        <h1 className='mt-5 text-5xl leading-[1.02] font-semibold text-white sm:text-6xl lg:text-7xl'>
                            {t.home.heroTitle}
                        </h1>
                        <p className='mt-6 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg'>
                            {t.home.heroText}
                        </p>
                        <div className='mt-8 flex flex-wrap gap-3'>
                            <Link href='/accommodations' className={buttonClass({ variant: 'gold', size: 'lg' })}>
                                {t.home.heroPrimary}
                                <ArrowRight className='size-4 rtl:rotate-180' aria-hidden='true' />
                            </Link>
                            <Link href='/booking' className={buttonClass({ variant: 'light', size: 'lg' })}>
                                {t.home.heroSecondary}
                            </Link>
                        </div>
                    </div>

                    <div className='mt-12 lg:mt-16'>
                        <QuickRequest locations={locations} today={base.today} />
                    </div>
                </div>
            </section>

            <section id='highlights' aria-label={t.home.heroEyebrow} className='border-b border-border bg-background'>
                <div className='mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8'>
                    {t.home.highlights.map((item, index) => {
                        const Icon = HIGHLIGHT_ICONS[index] ?? Gem

                        return (
                            <div key={item.title} className='flex gap-4'>
                                <span className='flex size-12 shrink-0 items-center justify-center rounded-full bg-gold-soft text-[oklch(0.5_0.09_70)]'>
                                    <Icon className='size-5' aria-hidden='true' />
                                </span>
                                <div>
                                    <h2 className='font-sans text-base font-bold text-navy'>{item.title}</h2>
                                    <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>{item.text}</p>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </section>

            <section id='featured' className='mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8'>
                <div className='flex flex-wrap items-end justify-between gap-6'>
                    <SectionHeading eyebrow={t.home.featuredEyebrow} title={t.home.featuredTitle} text={t.home.featuredText} />
                    <Link href='/accommodations' className={buttonClass({ variant: 'outline' })}>
                        {t.home.featuredAll}
                        <ArrowRight className='size-4 rtl:rotate-180' aria-hidden='true' />
                    </Link>
                </div>

                <div className='mt-12 grid gap-7 md:grid-cols-2 lg:grid-cols-3'>
                    {featured.map((item, index) => <AccommodationCard key={item.id} item={item} priority={index === 0} />)}
                </div>
            </section>

            <section id='about' className='bg-sand'>
                <div className='mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 md:py-28 lg:grid-cols-2 lg:gap-20 lg:px-8'>
                    <div className='relative'>
                        <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] shadow-2xl'>
                            <Image
                                src='/images/about-tunisia.jpg'
                                alt={t.home.aboutImageAlt}
                                fill
                                sizes='(min-width: 1024px) 40vw, 100vw'
                                className='object-cover'
                            />
                        </div>
                        <div className='absolute -bottom-6 end-6 hidden rounded-2xl bg-navy px-6 py-5 text-white shadow-xl sm:block'>
                            <p className='font-heading text-3xl font-semibold' dir='ltr'>El Mouensa</p>
                            <p className='mt-1 text-xs tracking-[0.2em] text-gold uppercase' dir='ltr'>4144 Zarzis</p>
                        </div>
                    </div>

                    <div>
                        <SectionHeading eyebrow={t.home.aboutEyebrow} title={t.home.aboutTitle} />
                        <p className='mt-6 text-base leading-relaxed text-foreground/80 md:text-lg'>{t.home.aboutText1}</p>
                        <p className='mt-4 text-base leading-relaxed text-foreground/80 md:text-lg'>{t.home.aboutText2}</p>
                        <ul className='mt-8 grid gap-3 sm:grid-cols-2'>
                            {t.home.aboutPoints.map(point => (
                                <li key={point} className='flex items-start gap-3 text-sm font-semibold text-navy'>
                                    <span className='mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-gold text-gold-foreground'>
                                        <Check className='size-3' aria-hidden='true' />
                                    </span>
                                    {point}
                                </li>
                            ))}
                        </ul>
                        <Link href='/contact' className={buttonClass({ variant: 'primary', className: 'mt-10' })}>
                            {t.nav.contact}
                            <ArrowRight className='size-4 rtl:rotate-180' aria-hidden='true' />
                        </Link>
                    </div>
                </div>
            </section>

            <section id='categories' className='mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8'>
                <SectionHeading eyebrow={t.home.categoriesEyebrow} title={t.home.categoriesTitle} align='center' />

                <div className='mt-12 grid gap-6 md:grid-cols-3'>
                    {categories.map(category => (
                        <Link
                            key={category.href}
                            href={category.href}
                            className='group flex flex-col gap-4 rounded-3xl border border-border bg-card p-8 shadow-sm transition-all hover:-translate-y-1 hover:border-gold/50 hover:shadow-xl focus-visible:outline-2 focus-visible:outline-gold'
                        >
                            <span className='flex size-14 items-center justify-center rounded-2xl bg-navy text-gold'>
                                <category.icon className='size-6' aria-hidden='true' />
                            </span>
                            <h3 className='text-2xl font-semibold text-navy'>{category.title}</h3>
                            <p className='text-sm leading-relaxed text-muted-foreground'>{category.text}</p>
                            <span className='mt-auto flex items-center justify-between border-t border-border pt-4 text-sm font-bold text-navy'>
                                {plural(category.count, t.common.accommodationOne, t.common.accommodationOther)}
                                <ArrowRight className='size-4 text-gold transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1' aria-hidden='true' />
                            </span>
                        </Link>
                    ))}
                </div>
            </section>

            <section id='how-it-works' className='bg-navy text-white'>
                <div className='mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8'>
                    <SectionHeading eyebrow={t.home.stepsEyebrow} title={t.home.stepsTitle} tone='light' align='center' />

                    <ol className='mt-14 grid gap-10 md:grid-cols-3'>
                        {t.home.steps.map((step, index) => {
                            const Icon = STEP_ICONS[index] ?? Send

                            return (
                                <li key={step.title} className='relative flex flex-col items-center text-center'>
                                    <span className='relative flex size-20 items-center justify-center rounded-full border border-gold/40 bg-white/5'>
                                        <Icon className='size-8 text-gold' aria-hidden='true' />
                                        <span className='absolute -end-1 -top-1 flex size-7 items-center justify-center rounded-full bg-gold text-xs font-bold text-gold-foreground'>{index + 1}</span>
                                    </span>
                                    <h3 className='mt-6 text-2xl font-semibold'>{step.title}</h3>
                                    <p className='mt-3 max-w-xs text-sm leading-relaxed text-white/70'>{step.text}</p>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </section>

            <section id='destinations' className='relative isolate overflow-hidden'>
                <Image src='/images/beach-tunisia.jpg' alt='' fill sizes='100vw' className='-z-20 object-cover' />
                <div aria-hidden='true' className='absolute inset-0 -z-10 bg-gradient-to-b from-navy/70 via-navy/55 to-navy/80' />

                <div className='mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8'>
                    <SectionHeading eyebrow={t.home.destinationsEyebrow} title={t.home.destinationsTitle} tone='light' />

                    <div className='mt-12 grid gap-6 md:grid-cols-2'>
                        {t.home.destinations.map((destination, index) => (
                            <Link
                                key={destination.name}
                                href={`/accommodations?location=${encodeURIComponent(DESTINATION_KEYS[index] ?? '')}`}
                                className='group rounded-3xl border border-white/20 bg-white/10 p-8 text-white backdrop-blur-md transition-colors hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-gold'
                            >
                                <p className='flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-gold-soft uppercase'>
                                    <MapPin className='size-4' aria-hidden='true' />
                                    {t.home.destinationsEyebrow}
                                </p>
                                <h3 className='mt-3 text-4xl font-semibold'>{destination.name}</h3>
                                <p className='mt-3 max-w-md text-sm leading-relaxed text-white/80'>{destination.text}</p>
                                <span className='mt-6 inline-flex items-center gap-2 text-sm font-bold text-gold-soft'>
                                    {t.nav.accommodations}
                                    <ArrowRight className='size-4 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1' aria-hidden='true' />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section id='customer-account' className='mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8'>
                <div className='grid items-center gap-12 overflow-hidden rounded-[2rem] border border-border bg-card p-8 shadow-sm md:p-12 lg:grid-cols-[1.1fr_1fr]'>
                    <div>
                        <SectionHeading eyebrow={t.home.accountEyebrow} title={t.home.accountTitle} text={t.home.accountText} />
                        <div className='mt-8 flex flex-wrap gap-3'>
                            {base.viewer
                                ? <Link href='/account' className={buttonClass({ variant: 'primary', size: 'lg' })}>{t.nav.account}</Link>
                                : (
                                    <>
                                        <Link href='/register' className={buttonClass({ variant: 'primary', size: 'lg' })}>{t.home.accountRegister}</Link>
                                        <Link href='/login' className={buttonClass({ variant: 'outline', size: 'lg' })}>{t.home.accountLogin}</Link>
                                    </>
                                )}
                        </div>
                    </div>

                    <ul className='grid gap-4 sm:grid-cols-2'>
                        {t.home.accountPoints.map(point => (
                            <li key={point} className='flex items-start gap-3 rounded-2xl bg-sand p-5 text-sm font-semibold text-navy'>
                                <Check className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                {point}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section id='request' className='px-4 pb-20 sm:px-6 md:pb-28 lg:px-8'>
                <div className='relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-navy px-6 py-16 text-center text-white md:px-12 md:py-20'>
                    <div aria-hidden='true' className='pointer-events-none absolute -start-20 -top-20 size-72 rounded-full bg-gold/20 blur-3xl' />
                    <div aria-hidden='true' className='pointer-events-none absolute -bottom-24 -end-16 size-72 rounded-full bg-gold/10 blur-3xl' />
                    <h2 className='relative mx-auto max-w-2xl text-3xl leading-tight font-semibold md:text-5xl'>{t.home.ctaTitle}</h2>
                    <p className='relative mx-auto mt-5 max-w-xl text-white/75'>{t.home.ctaText}</p>
                    <div className='relative mt-8 flex justify-center'>
                        <Link href='/booking' className={buttonClass({ variant: 'gold', size: 'lg' })}>
                            {t.home.ctaButton}
                            <ArrowRight className='size-4 rtl:rotate-180' aria-hidden='true' />
                        </Link>
                    </div>
                </div>
            </section>
        </SiteLayout>
    )
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async (ctx) => {
    const locale = localeOf(ctx)

    const [base, accommodations, counts, locations] = await Promise.all([
        loadPublicProps(ctx),
        listAccommodations(locale).catch(() => []),
        countByCategory().catch(() => ({ poolVillas: 0, houses: 0, apartments: 0 })),
        listLocations().catch(() => [])
    ])

    const featured = [...accommodations]
        .sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured))
        .slice(0, 3)

    return { props: { ...base, featured, counts, locations } }
}

export default Home
